if ( typeof ProductImageZoom !== 'function' ) {
	class ProductImageZoom extends HTMLElement {

		constructor(){

			super();

			if ( ! document.getElementById('zoom') ) {
				this.zoom = document.createElement('div');
				this.zoom.id = 'zoom';
				this.zoom.innerHTML = `
					<img />
					<span class="zoom__exit">${KROWN.settings.symbols.zoom_out}</span>
					<span class="zoom__loader">${KROWN.settings.symbols.zoom_loader}</span>
					<div class="zoom__overlay"></div>
				`;
				document.body.append(this.zoom);

				// The fullscreen viewer is shared by every gallery image, so close it through whichever instance opened it
				const closeActive = ()=>{
					if ( this.zoom._activeZoom ) {
						this.zoom._activeZoom._productZoomUnmount();
					}
				};
				this.zoom.querySelector('.zoom__exit').addEventListener('click', closeActive);
				// On desktop the image follows the cursor, so a click anywhere closes the viewer (touch devices pan instead)
				this.zoom.addEventListener('click', e=>{
					if ( ! e.target.closest('.zoom__exit') && ProductImageZoom.canHover() ) {
						closeActive();
					}
				});
				document.addEventListener('keydown', e=>{
					if ( e.key == 'Escape' && this.zoom.classList.contains('opened') ) {
						closeActive();
					}
				});
			} else {
				this.zoom = document.getElementById('zoom');
			}

			this.onMouseMoveHandlerBinded = this.onMouseMoveHandler.bind(this);
			this.onResizeHandlerBinded = this.onResizeHandler.bind(this);

			this.addEventListener('click', this.onClick.bind(this));

			// Hover to zoom (desktop only)

			this.lens = this.querySelector('.product-image-zoom__lens');
			if ( this.lens ) {
				this.style.setProperty('--zoom-scale', ProductImageZoom.scale);
				this.addEventListener('mouseenter', this.onLensEnter.bind(this));
				this.addEventListener('mousemove', this.onLensMove.bind(this));
				this.addEventListener('mouseleave', this.onLensLeave.bind(this));

				// Tap to zoom inside the box and drag to pan (touch devices only)
				this.addEventListener('touchstart', this.onTouchStart.bind(this), {passive:true});
				this.addEventListener('touchmove', this.onTouchMove.bind(this), {passive:false});
				this.addEventListener('touchend', this.onTouchEnd.bind(this), {passive:true});
				this.onOutsideTouchBinded = e=>{
					if ( ! this.contains(e.target) ) this.touchZoomOut();
				};
				// Zoom back out once the slider moves to a different image
				this.closest('css-slider')?.addEventListener('change', e=>{
					const item = this.closest('.product-gallery-item');
					if ( ! item || e.target.index != item.dataset.index ) this.touchZoomOut();
				});
			}

			// Keyboard access: Enter / Space on the focusable gallery item opens the zoom

			const galleryItem = this.closest('.product-gallery-item');
			if ( galleryItem ) {
				galleryItem.addEventListener('keydown', e=>{
					if ( ( e.key == 'Enter' || e.key == ' ' ) && e.target == galleryItem ) {
						e.preventDefault();
						this.open();
					}
				});
			}

		}

		static canHover(){
			return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
		}

		// A slide that is only partly visible (the slider "peek") acts as a next/prev trigger instead of zooming

		isPeeking(){
			const slider = this.closest('css-slider');
			const holder = slider && slider.sliderEnabled ? slider.querySelector('.css-slider-holder') : null;
			if ( ! holder ) return false;
			const item = ( this.closest('.product-gallery-item') || this ).getBoundingClientRect();
			const view = holder.getBoundingClientRect();
			return item.left < view.left - 2 || item.right > view.right + 2;
		}

		onClick(){
			if ( this._suppressClick ) {
				this._suppressClick = false;
				return;
			}
			if ( this.isPeeking() ) {
				const item = this.closest('.product-gallery-item');
				// The product gallery snaps between images everywhere else; clicking a peeking slide is the one place it animates
				if ( item ) this.closest('css-slider').changeSlide(item.dataset.index, 'smooth');
				// The cursor usually stays put while the slide scrolls in, so start the hover zoom once it has settled
				setTimeout(()=>{
					if ( this.pointerInside && this._lastPointer ) this.updateHoverState(this._lastPointer);
				}, 600);
				return;
			}
			if ( ! ProductImageZoom.canHover() && this.lens ) {
				// Touch: tap toggles zoom inside the image box instead of opening the fullscreen viewer
				if ( this.touchZoomed ) this.touchZoomOut();
				else this.touchZoomIn(this._lastTap);
				return;
			}
			this.open();
		}

		touchZoomIn(point){
			this.touchZoomed = true;
			this.classList.add('product-image-zoom--touch-zoomed');
			if ( point ) {
				this._setOrigin(this._mapToOrigin(point.clientX, point.clientY));
			} else {
				this._setOrigin({x:50, y:50});
			}
			this._showLens();
			document.addEventListener('touchstart', this.onOutsideTouchBinded, {passive:true});
		}

		touchZoomOut(){
			if ( ! this.touchZoomed ) return;
			this.touchZoomed = false;
			this.classList.remove('product-image-zoom--touch-zoomed');
			this._lensOff();
			document.removeEventListener('touchstart', this.onOutsideTouchBinded);
		}

		onTouchStart(e){
			const t = e.touches[0];
			this._lastTap = { clientX: t.clientX, clientY: t.clientY };
			this._touchStart = { x: t.clientX, y: t.clientY, origin: { ...( this._origin || {x:50, y:50} ) } };
			this._touchMoved = false;
			this._suppressClick = false;
		}

		onTouchMove(e){
			if ( ! this.touchZoomed || ! this._touchStart || e.touches.length > 1 ) return;
			// Keep the slider and the page still while panning the zoomed image
			e.preventDefault();
			const t = e.touches[0];
			const dx = t.clientX - this._touchStart.x;
			const dy = t.clientY - this._touchStart.y;
			if ( Math.abs(dx) > 5 || Math.abs(dy) > 5 ) this._touchMoved = true;
			// Move the image with the finger: at scale s, shifting the transform origin by d moves the content by d * (s - 1)
			const rect = this.getBoundingClientRect();
			const k = ProductImageZoom.scale - 1;
			this._setOrigin({
				x: this._touchStart.origin.x - dx / ( rect.width * k ) * 100,
				y: this._touchStart.origin.y - dy / ( rect.height * k ) * 100
			});
		}

		onTouchEnd(){
			// A drag that pans the image shouldn't also count as a tap that zooms back out
			if ( this.touchZoomed && this._touchMoved ) this._suppressClick = true;
			this._touchStart = null;
		}

		// Only the central 85% of the image steers the zoom, so the edges are reachable without
		// putting the cursor right on the border
		_mapToOrigin(clientX, clientY){
			const area = 0.85;
			const inset = ( 1 - area ) / 2;
			const rect = this.getBoundingClientRect();
			return {
				x: ( ( clientX - rect.left ) / rect.width - inset ) / area * 100,
				y: ( ( clientY - rect.top ) / rect.height - inset ) / area * 100
			};
		}

		// Position the zoomed image so the point at origin (in % of the image) lines up with the same % of the box
		_setOrigin(o){
			this._origin = { x: Math.min(Math.max(o.x, 0), 100), y: Math.min(Math.max(o.y, 0), 100) };
			const k = ProductImageZoom.scale - 1;
			this.lens.style.setProperty('--zoom-tx', `${-this._origin.x / 100 * this.offsetWidth * k}px`);
			this.lens.style.setProperty('--zoom-ty', `${-this._origin.y / 100 * this.offsetHeight * k}px`);
			this.lens.style.setProperty('--zoom-ox', `${this._origin.x}%`);
			this.lens.style.setProperty('--zoom-oy', `${this._origin.y}%`);
		}

		_lensOn(){
			this.classList.add('product-image-zoom--hovering');
			clearTimeout(this._settleTimer);
			this._settleTimer = setTimeout(()=>this.classList.add('product-image-zoom--settled'), 220);
		}

		_lensOff(){
			clearTimeout(this._settleTimer);
			this.classList.remove('product-image-zoom--settled');
			this.classList.remove('product-image-zoom--hovering');
		}

		// Zoom starts instantly using the image already shown in the gallery, then swaps to the
		// high-res file once it has downloaded and decoded
		_showLens(){

			if ( ! this.lens.getAttribute('src') ) {
				const base = this.closest('.product-gallery-item')?.querySelector('img.product-media-image');
				if ( base && base.complete && base.naturalWidth > 0 ) {
					this.lens.src = base.currentSrc || base.src;
				}
			}

			if ( this.lens.getAttribute('src') ) {
				this._lensOn();
			}

			if ( ! this._hiResRequested ) {
				this._hiResRequested = true;
				const hiRes = new Image();
				const swap = ()=>{
					this.lens.src = hiRes.src;
					this.classList.add('product-image-zoom--hi-res');
					if ( ( this.hovering || this.touchZoomed ) && ! this.classList.contains('product-image-zoom--hovering') ) this._lensOn();
				};
				hiRes.onload = ()=>{
					// decode() first so the swap doesn't flash a blank frame
					if ( hiRes.decode ) hiRes.decode().then(swap, swap);
					else swap();
				};
				hiRes.src = this.getAttribute('data-image');
			}

		}

		open(){
			this.onLensLeave();
			this.zoom._activeZoom = this;
			this.zoom.classList.add('opened');
			this.image = this.zoom.querySelector('img');
			this.image.src = this.getAttribute('data-image');
			if ( this.image.complete && this.image.naturalWidth > 0 ) {
				this._productZoomMount();
			} else {
				this.image.addEventListener('load', this._productZoomMount.bind(this), {once:true});
			}
		}

		onLensEnter(e){
			if ( ! ProductImageZoom.canHover() ) return;
			this.pointerInside = true;
			this.updateHoverState(e);
		}

		// Re-checked on every mouse move, so zoom kicks in as soon as a peeking slide scrolls fully into view
		updateHoverState(e){
			const peeking = this.isPeeking();
			this.classList.toggle('product-image-zoom--peek', peeking);
			if ( peeking ) {
				if ( this.hovering ) this.onLensLeave();
				this.pointerInside = true;
				return;
			}
			if ( this.hovering ) return;
			this.hovering = true;
			this._showLens();
			this.onLensMove(e);
		}

		onLensMove(e){
			this._lastPointer = { clientX: e.clientX, clientY: e.clientY };
			if ( this.pointerInside ) this.updateHoverState(e);
			if ( ! this.hovering ) return;
			this._setOrigin(this._mapToOrigin(e.clientX, e.clientY));
		}

		onLensLeave(e){
			// Mobile browsers fire emulated mouse events on tap; they must not cancel the touch zoom
			if ( this.touchZoomed ) return;
			if ( e && e.type == 'mouseleave' ) {
				this.pointerInside = false;
				this.classList.remove('product-image-zoom--peek');
			}
			this.hovering = false;
			this._lensOff();
		}

		onMouseMoveHandler(e){
			window.clientX = e.clientX;
			window.clientY = e.clientY;
			const x = e.clientX * ( window.innerWidth - this.image.offsetWidth ) / window.innerWidth;
			const y = e.clientY * ( window.innerHeight - this.image.offsetHeight ) / window.innerHeight;
			this.image.style.left = x + 'px';
			this.image.style.top = y + 'px';
		}

		onResizeHandler(){

			const rf = window.innerWidth > 768 ? 1 : 2;

			if ( this.image.classList.contains('portrait') ) {
				this.image.style.width = ( window.innerWidth*rf ) + 'px';
				this.image.style.height = ( window.innerWidth*rf / this.image.dataset.ratio ) + 'px';
			} else {

				this.image.style.height = ( window.innerHeight*rf ) + 'px';
				this.image.style.width = ( window.innerHeight*rf * this.image.dataset.ratio ) + 'px';

				if ( this.image.offsetWidth < window.innerWidth ) {
					this.image.style.width = ( window.innerWidth*rf ) + 'px';
					this.image.style.height = ( window.innerWidth*rf / this.image.dataset.ratio ) + 'px';
				}

			}

		}

		_productZoomMount() {

			this.image.style.left = 0;
			this.image.style.top = 0;

			window.addEventListener('mousemove', this.onMouseMoveHandlerBinded, {passive:true});

			this.image.dataset.ratio = this.image.naturalWidth / this.image.naturalHeight;
			window.addEventListener('resize', this.onResizeHandlerBinded, {passive:true});
			this.onResizeHandler();

			// Start the image under the cursor's current position instead of the top-left corner
			if ( window.clientX !== undefined ) {
				this.onMouseMoveHandler({clientX: window.clientX, clientY: window.clientY});
			}

			setTimeout(()=>{
				if ( document.body.classList.contains('touch') || document.body.classList.contains('touchevents') ) {
					this.zoom.scrollTop = ( window.innerHeight - this.image.offsetHeight ) / -2;
					this.zoom.scrollLeft = ( window.innerWidth - this.image.offsetWidth ) / -2;
				}
				this.zoom.classList.add('loaded');
			}, 50);

		}

		_productZoomUnmount() {

			this.zoom.classList.remove('opened');
			this.zoom._activeZoom = null;

			setTimeout(()=>{
				window.removeEventListener('resize', this.onResizeHandlerBinded);
				window.removeEventListener('mousemove', this.onMouseMoveHandlerBinded);
				const image = document.querySelector('#zoom img');
				image.src = '';
				image.className = '';
				image.style = '';
				this.zoom.classList.remove('loaded');
			}, 300);

		}

	}

	// Zoom level; passed to the CSS as --zoom-scale
	ProductImageZoom.scale = 2.5;

	window.ProductImageZoom = ProductImageZoom;

  if ( typeof customElements.get('product-image-zoom') == 'undefined' ) {
		customElements.define('product-image-zoom', ProductImageZoom);
	}

}
